import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Quotation,
  QuotationCoverage,
  QuotationSummaryResponse,
  QuotationUnit,
  Total,
} from 'data-layer-cr/dist/models';
import { makeEmptyQuotation } from 'data-layer-cr/dist/models/Quotation';
import { makeEmptyTotal } from 'data-layer-cr/dist/models/Total';

import { Plan } from 'src/components/screens/Quotations/components/OptionPriceCard/SelectPlan';
import SubAgentItem from 'src/models/Quotation/SubAgentItem';
import CarItemFromLicense, {
  makeEmpty,
} from '../../../models/Quotation/CarItemFromLicense';
import QuotationChange from '../../../models/Quotation/QuotationChange';
import PricingProfileResponse, {
  MakeEmpty as emptyPricingProfileResponse,
} from '../../../models/responseTypes/PricingProfileResponse';

type UpdateUnitCoverageData = {
  packageOption: number;
  packageSelected: string;
  newCoverages: QuotationCoverage[];
};

type UpdateCoveragePricingData = UpdateUnitCoverageData & {
  newTotal: Total;
};

export type StartCopyQuotationData = {
  quotationId: number;
  quotationHash: string;
};

export type CopyQuotationData = StartCopyQuotationData & {
  openModal: boolean;
  copyingQuotation: boolean;
};

export type QuotationState = {
  currentQuotation: Quotation;
  currentQuotationChanges: QuotationChange[];
  quotationList: QuotationSummaryResponse[];
  usedLicense: string;
  usedProfile: number;
  currentProfile: PricingProfileResponse;
  usedAdditionalBroker: string[];
  newQuotationModalIsOpen: boolean;
  inspectionReportBase64: string;
  vehicleRegisterBase64: string;
  carItemFromLicense: CarItemFromLicense;

  cloningQuotationId: number;
  isLicenseAutoPx: boolean;

  totalPricePreview: Total;
  subAgentList: SubAgentItem[];
  usedsubAgentLicense: string;
  savedQuotationUnit: QuotationUnit[];
  copyQuotationData: CopyQuotationData;
  plans: Plan[];
};

export const emptyQuotationWithMultipleOptions: Quotation = {
  ...makeEmptyQuotation(),
  isAutoPx: true,
  hasMultipleOptions: true,
};

export const initialState: QuotationState = {
  currentQuotation: { ...emptyQuotationWithMultipleOptions },
  currentQuotationChanges: [],
  quotationList: [],
  usedLicense: '',
  usedProfile: 0,
  currentProfile: emptyPricingProfileResponse(),
  usedAdditionalBroker: [],
  newQuotationModalIsOpen: false,
  inspectionReportBase64: '',
  vehicleRegisterBase64: '',
  cloningQuotationId: -1,
  carItemFromLicense: makeEmpty(),
  totalPricePreview: { ...makeEmptyTotal(), grandTotal: -1 },
  /**
   * Since special plans can now be autoPx, we use this to validate if the chosen license
   * is autoPx, this way we can assure that specialPlans only applies to the car models from
   * those special plans.
   * * This was used to solve the issue where you had a license with no autopx
   * * but with a special profile, you could advance from carForm with autoPx
   * * and then go back with a car model without autoPx but it would show autoPx
   * * because of the previous validation. (this issue is just with ASE)
   * */
  isLicenseAutoPx: false,
  subAgentList: [],
  usedsubAgentLicense: '',
  savedQuotationUnit: [],
  copyQuotationData: {
    copyingQuotation: false,
    openModal: false,
    quotationHash: '',
    quotationId: -1,
  },
  plans: [],
};

const quotationSlice = createSlice({
  name: 'quotation',
  initialState,
  reducers: {
    overwriteCurrentQuotation: (
      state,
      action: PayloadAction<Quotation>,
    ): void => {
      state.currentQuotation = action.payload;
    },
    updateCurrentQuotationChanges: (
      state,
      action: PayloadAction<QuotationChange[]>,
    ): void => {
      state.currentQuotationChanges = action.payload;
    },
    updateCarItemFromLicense: (
      state,
      action: PayloadAction<CarItemFromLicense>,
    ): void => {
      state.carItemFromLicense = action.payload;
    },
    updateQuotationSummaryList: (
      state,
      action: PayloadAction<QuotationSummaryResponse[]>,
    ): void => {
      state.quotationList = action.payload;
    },
    updateUsedLicense: (state, action: PayloadAction<string>): void => {
      state.usedLicense = action.payload;
    },
    updateUsedProfile: (state, action: PayloadAction<number>): void => {
      state.usedProfile = action.payload;
    },
    updateCurrentProfile: (
      state,
      action: PayloadAction<PricingProfileResponse>,
    ): void => {
      state.currentProfile = action.payload;
    },
    updateUsedAdditionalBroker: (
      state,
      action: PayloadAction<string[]>,
    ): void => {
      state.usedAdditionalBroker = action.payload;
    },
    updateQuotationModalIsOpen: (
      state,
      action: PayloadAction<boolean>,
    ): void => {
      state.newQuotationModalIsOpen = action.payload;
    },
    updateInspectionReport: (state, action: PayloadAction<string>): void => {
      state.inspectionReportBase64 = action.payload;
    },
    updateVehicleRegister: (state, action: PayloadAction<string>): void => {
      state.vehicleRegisterBase64 = action.payload;
    },
    updateCloningQuotationId: (state, action: PayloadAction<number>): void => {
      state.cloningQuotationId = action.payload;
    },
    updateIsLicenseAutoPx: (state, action: PayloadAction<boolean>): void => {
      state.isLicenseAutoPx = action.payload;
    },
    updateTotalPricePreview: (state, action: PayloadAction<Total>): void => {
      state.totalPricePreview = action.payload;
    },
    updateCoveragePricing: (
      state,
      action: PayloadAction<UpdateCoveragePricingData>,
    ): void => {
      state.currentQuotation.units[action.payload.packageOption].coverages =
        action.payload.newCoverages;
      state.currentQuotation.units[action.payload.packageOption].total =
        action.payload.newTotal;
    },
    updateUnitCoverages: (
      state,
      action: PayloadAction<UpdateUnitCoverageData>,
    ): void => {
      state.currentQuotation.units[action.payload.packageOption].coverages =
        action.payload.newCoverages;
    },
    updateSubagents: (state, action: PayloadAction<SubAgentItem[]>): void => {
      state.subAgentList = action.payload;
    },
    updateUsedSubAgentLicense: (state, action: PayloadAction<string>): void => {
      state.usedsubAgentLicense = action.payload;
    },
    updateSavedQuotationUnit: (
      state,
      action: PayloadAction<QuotationUnit[]>,
    ): void => {
      state.savedQuotationUnit = action.payload;
    },
    cleanCopyQuotationData: (state): void => {
      state.copyQuotationData = {
        copyingQuotation: false,
        openModal: false,
        quotationHash: '',
        quotationId: -1,
      };
    },
    startCopyQuotationData: (
      state,
      action: PayloadAction<StartCopyQuotationData>,
    ): void => {
      state.copyQuotationData = {
        ...action.payload,
        copyingQuotation: true,
        openModal: true,
      };
    },
    closeCopyQuotationModal: (state): void => {
      state.copyQuotationData.openModal = false;
    },
    updatePlans: (state, action): void => {
      state.plans = action.payload;
    },
  },
});

export const {
  overwriteCurrentQuotation,
  updateCurrentQuotationChanges,
  updateCarItemFromLicense,
  updateQuotationSummaryList,
  updateUsedLicense,
  updateUsedProfile,
  updateCurrentProfile,
  updateUsedAdditionalBroker,
  updateQuotationModalIsOpen,
  updateCloningQuotationId,
  updateInspectionReport,
  updateVehicleRegister,
  updateIsLicenseAutoPx,
  updateTotalPricePreview,
  updateCoveragePricing,
  updateSubagents,
  updateUsedSubAgentLicense,
  updateSavedQuotationUnit,
  updateUnitCoverages,
  cleanCopyQuotationData,
  startCopyQuotationData,
  closeCopyQuotationModal,
  updatePlans,
} = quotationSlice.actions;

export default quotationSlice.reducer;
